import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { fetchHealth, type HealthSnapshot } from './health';

interface HealthContextValue extends HealthSnapshot {
  refreshing: boolean;
  refresh: () => void;
}

const HealthContext = createContext<HealthContextValue | null>(null);

const INITIAL: HealthSnapshot = { state: 'checking', checks: [], checkedAt: null };

interface Props {
  children: ReactNode;
  intervalMs?: number;
}

/**
 * Single shared poller for system readiness. Polling pauses while the tab is
 * hidden so idle operator consoles do not generate background load.
 */
export function HealthProvider({ children, intervalMs = 15000 }: Props) {
  const [snapshot, setSnapshot] = useState<HealthSnapshot>(INITIAL);
  const [refreshing, setRefreshing] = useState(false);
  const inflight = useRef<AbortController | null>(null);

  // State is only updated from promise callbacks, never synchronously.
  const load = useCallback(() => {
    inflight.current?.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    fetchHealth(ctrl.signal)
      .then((s) => {
        setSnapshot(s);
      })
      .catch(() => {
        /* aborted by a newer request or unmount */
      })
      .finally(() => {
        if (inflight.current === ctrl) {
          inflight.current = null;
          setRefreshing(false);
        }
      });
  }, []);

  /** Explicit user-initiated refresh; drives the "refreshing" indicator. */
  const refresh = useCallback(() => {
    setRefreshing(true);
    load();
  }, [load]);

  useEffect(() => {
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      inflight.current?.abort();
    };
  }, [load, intervalMs]);

  return <HealthContext value={{ ...snapshot, refreshing, refresh }}>{children}</HealthContext>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHealth(): HealthContextValue {
  const ctx = use(HealthContext);
  if (!ctx) throw new Error('useHealth must be used inside <HealthProvider>');
  return ctx;
}
